// Card content is user-authored HTML: Quill output, Anki imports, curated and
// shared decks. Everything that renders it goes through here so that script,
// frames and exfiltrating CSS never reach another user's browser, while the
// markup real cards rely on (Quill formatting, cloze spans, multiple-choice
// inputs, image-occlusion overlays, audio, SVG, per-card CSS) survives.
import sanitizeHtml from 'sanitize-html';
import parse from 'html-react-parser';

export const MEDIA_HOST = 'd1lze488p2cr08.cloudfront.net';

const SVG_SHAPES = ['svg', 'g', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'path', 'text', 'tspan', 'defs', 'clippath', 'mask'];
const SVG_ATTRS = ['viewbox', 'xmlns', 'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry', 'd', 'points', 'fill', 'fill-opacity',
    'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray', 'opacity', 'transform', 'text-anchor',
    'font-size', 'font-family', 'font-weight', 'preserveaspectratio', 'clip-path', 'dominant-baseline'];

const ALLOWED_TAGS = sanitizeHtml.defaults.allowedTags.concat([
    'img', 'audio', 'video', 'source', 'label', 'input', 'button', 'font', 'center', 'u', 's', 'mark', 'small', 'big', 'del', 'ins',
    'details', 'summary', 'style',
]).concat(SVG_SHAPES);

const ALLOWED_ATTRIBUTES = {
    '*': ['class', 'id', 'style', 'title', 'dir', 'lang', 'data-*', 'aria-*', 'width', 'height', 'align', 'valign', 'border', 'cellpadding', 'cellspacing', 'role', 'bgcolor', 'nowrap'],
    a: ['href', 'name', 'target', 'rel'],
    img: ['src', 'srcset', 'alt', 'loading'],
    audio: ['src', 'controls', 'preload', 'loop'],
    video: ['src', 'controls', 'preload', 'loop', 'muted', 'playsinline', 'poster'],
    source: ['src', 'type'],
    input: ['type', 'name', 'value', 'checked', 'disabled', 'readonly', 'placeholder'],
    label: ['for'],
    button: ['type', 'disabled'],
    td: ['colspan', 'rowspan'],
    th: ['colspan', 'rowspan', 'scope'],
    font: ['color', 'size', 'face'],
    table: ['summary'],
    details: ['open'],
    ol: ['type', 'start', 'reversed'], // quiz options are lettered
    ul: ['type'],
    li: ['value'],
};
SVG_SHAPES.forEach((tag) => { ALLOWED_ATTRIBUTES[tag] = SVG_ATTRS; });

import postcss from 'postcss';
import safeParser from 'postcss-safe-parser';
import valueParser from 'postcss-value-parser';

// CSS from cards (inline style attributes, Anki <style> blocks) is parsed with
// a real CSS parser rather than rewritten with regexes, so strings, escapes,
// comments and unterminated input are handled the way a browser would. Rules:
//   - at-rules other than @media/@supports/@keyframes/@page/@layer/@container
//     are dropped (@import, @font-face, @namespace, @charset, ...)
//   - a declaration is dropped if it references a resource anywhere but our
//     media host or an inline image (url(), image-set(), cross-fade(),
//     element(), paint(), src(), image()), or uses expression()/behavior/
//     -moz-binding
//   - comments are dropped; '<' is escaped in the output so the text can
//     never close the <style> element it is placed in
// Anything the parser rejects is dropped entirely.
const SAFE_AT_RULES = new Set(['media', 'supports', 'keyframes', '-webkit-keyframes', '-moz-keyframes', 'page', 'layer', 'container']);
const LOADER_FUNCTIONS = new Set(['url', 'image-set', '-webkit-image-set', '-moz-image-set', 'image', 'cross-fade', '-webkit-cross-fade', 'element', '-moz-element', 'paint', 'src']);
const BLOCKED_FUNCTIONS = new Set(['expression']);
const BLOCKED_PROPERTIES = new Set(['behavior', '-moz-binding', '-ms-behavior']);
const URL_LIKE = /(?:^|[^\w-])[a-z][a-z0-9+.-]*:\/\/|(?:^|[\s,(])\/\//i;

// Decodes the CSS escapes that spell an ASCII letter or '-', so "\\75rl(" and
// "\\75\\72 l(" are seen as "url(" by the tokenizer below exactly as a browser
// sees them. Every other escape (\\A newlines, \\201C glyphs, \\" quotes, \\31
// digits in selectors) is left untouched, so strings and selectors keep their
// meaning. Decoding a letter escape is lossless inside strings too.
const LETTER = /^[A-Za-z-]$/;
export const normalizeCssEscapes = (css) => String(css)
    .replace(/\\([0-9a-fA-F]{1,6})\s?/g, (m, hex) => {
        const code = parseInt(hex, 16);
        const ch = code > 0 && code < 0x110000 && !(code >= 0xd800 && code <= 0xdfff) ? String.fromCodePoint(code) : '\ufffd';
        return LETTER.test(ch) ? ch : m;
    })
    .replace(/\\([G-Zg-z-])/g, '$1'); // single-char escapes of a-f are hex escapes, handled above
const decodeIdent = (ident) => normalizeCssEscapes(ident).toLowerCase();
// Full decode (every escape), used only for the address check, never for output.
const decodeAllEscapes = (css) => String(css)
    .replace(/\\([0-9a-fA-F]{1,6})\s?/g, (m, hex) => { const code = parseInt(hex, 16); return code > 0 && code < 0x110000 && !(code >= 0xd800 && code <= 0xdfff) ? String.fromCodePoint(code) : '\ufffd'; })
    .replace(/\\(.)/g, '$1');

const allowedResource = (target) => {
    const t = String(target).trim();
    return t.startsWith(`https://${MEDIA_HOST}/`) || /^data:image\//i.test(t) || t.startsWith('#');
};

// True when a declaration value may be kept.
const safeValue = (value) => {
    if (value === undefined || value === null) return true;
    let safe = true;
    const parsed = valueParser(String(value));
    const allowedUrls = [];
    parsed.walk((node) => {
        if (!safe) return false;
        if (node.type === 'comment') { safe = false; return false; }
        if (node.type !== 'function') return undefined;
        const name = decodeIdent(node.value);
        if (BLOCKED_FUNCTIONS.has(name)) { safe = false; return false; }
        // value-parser folds a leading delimiter into the name (".url", "{url")
        if (name !== 'url' && /url$/.test(name)) { safe = false; return false; }
        if (name === 'url') {
            const first = node.nodes.find((n) => n.type === 'string' || n.type === 'word');
            if (!first || !allowedResource(first.value)) safe = false;
            else allowedUrls.push(node);
            return false;
        }
        if (LOADER_FUNCTIONS.has(name)) { safe = false; return false; }
        // no other function ever takes an address: a scheme or "//" inside one
        // is an attempt to smuggle a resource reference past the url() check
        if (URL_LIKE.test(valueParser.stringify(node.nodes))) { safe = false; return false; }
        return undefined;
    });
    if (!safe) return false;
    // outside an allowed url(), an address has no business anywhere in a value
    allowedUrls.forEach((node) => { node.type = 'word'; node.value = 'ok'; node.nodes = []; });
    return !URL_LIKE.test(decodeAllEscapes(valueParser.stringify(parsed.nodes)));
};

const scrubContainer = (container) => {
    container.each((node) => {
        if (node.type === 'comment') { node.remove(); return; }
        if (node.type === 'atrule') {
            if (!SAFE_AT_RULES.has(decodeIdent(node.name)) || !safeValue(node.params)) { node.remove(); return; }
            if (node.nodes) scrubContainer(node);
            return;
        }
        if (node.type === 'rule') { scrubContainer(node); return; }
        if (node.type === 'decl') {
            // postcss parks an IE-hack prefix ("*zoom", "_height") in raws.before;
            // wiping raws would promote it to a real declaration, so drop it
            if (/[*_]$/.test((node.raws && node.raws.before) || '')) { node.remove(); return; }
            const prop = decodeIdent(node.prop);
            if (!/^-{0,2}[a-z_][\w-]*$/i.test(prop) || BLOCKED_PROPERTIES.has(prop) || !safeValue(node.value)) { node.remove(); return; }
            // postcss keeps whatever sat between the value and "important" in a raw; serialise the standard form instead
            if (node.important && node.raws) delete node.raws.important;
        }
    });
};

// Serialise from the node model only: postcss keeps the original spelling of
// every node in "raws" and prefers it when printing, and with the lenient
// parser those raws can hold text no node represents. Every raw is wiped
// (cleanRaws() only drops the whitespace ones).
// Then only '<' needs escaping: it is what could close a <style> element.
// Nothing is ever deleted after parsing, since deleting a substring can join
// two harmless tokens into a harmful one ("u-->rl(").
const serialise = (root) => {
    root.walk((node) => { node.raws = {}; });
    root.raws = {};
    return root.toString().replace(/</g, '\\3c ');
};

export const sanitizeCardCss = (css) => {
    if (!css) return '';
    try {
        // HTML comment markers are legal noise at the top level of a stylesheet;
        // replace them with a space (never delete) before parsing. The safe
        // parser closes unclosed blocks and strings the way a browser would
        // instead of rejecting the whole sheet.
        const source = normalizeCssEscapes(css).replace(/<\/?style[^>]*>/gi, ' ').replace(/<!--|-->/g, ' ');
        const root = safeParser(source);
        scrubContainer(root);
        return serialise(root);
    } catch (e) {
        return ''; // malformed beyond repair, or too deeply nested to walk
    }
};

const addNoopener = (tagName, attribs) => {
    const next = { ...attribs };
    if (next.target && next.target !== '_blank') delete next.target;
    if (next.target === '_blank') next.rel = 'noopener noreferrer';
    return { tagName, attribs: next };
};

const SANITIZE_OPTIONS = {
    allowedTags: ALLOWED_TAGS,
    allowedAttributes: ALLOWED_ATTRIBUTES,
    allowedSchemes: ['http', 'https', 'mailto', 'tel'],
    allowedSchemesByTag: { img: ['http', 'https', 'data'], source: ['http', 'https'], audio: ['http', 'https'], video: ['http', 'https'] },
    allowedSchemesAppliedToAttributes: ['href', 'src', 'poster'],
    allowProtocolRelative: false,
    allowVulnerableTags: true, // <style>: its contents are rewritten below
    nonTextTags: ['script', 'textarea', 'option', 'style', 'button'],
    parseStyleAttributes: false, // style attributes are rewritten by sanitizeCardCss instead
    transformTags: {
        '*': (tagName, attribs) => {
            const next = { ...attribs };
            if (next.style !== undefined) next.style = sanitizeCardCss(next.style);
            return { tagName, attribs: next };
        },
        a: (tagName, attribs) => {
            const next = { ...attribs };
            if (next.style !== undefined) next.style = sanitizeCardCss(next.style);
            return addNoopener(tagName, next);
        },
    },
};

const STYLE_BLOCK = /<style([^>]*)>([\s\S]*?)<\/style>/gi;

// Card content never needs <button>: the browse table binds its row actions by
// class name, so a button inside a card could pose as one. Buttons are allowed
// only for the table's own actions column (see secureColumns).
const CONTENT_OPTIONS = { ...SANITIZE_OPTIONS, allowedTags: SANITIZE_OPTIONS.allowedTags.filter((t) => t !== 'button') };

// HTML string safe to hand to innerHTML or to html-react-parser.
export const sanitizeCardHtml = (html, options = CONTENT_OPTIONS) => {
    if (html === null || html === undefined) return '';
    const clean = sanitizeHtml(String(html), options);
    return clean.replace(STYLE_BLOCK, (m, attrs, css) => `<style>${sanitizeCardCss(css)}</style>`);
};

export const sanitizeActionHtml = (html) => sanitizeCardHtml(html, SANITIZE_OPTIONS);

// Per-card CSS stored as "<style>...</style>" by the Anki importer.
export const sanitizeCardStyle = (css) => {
    if (!css) return '';
    return `<style>${sanitizeCardCss(css)}</style>`;
};

export const renderCardHtml = (html) => parse(sanitizeCardHtml(html));
export const renderCardCss = (css) => parse(sanitizeCardStyle(css));

export const escapeHtml = (value) => String(value === null || value === undefined ? '' : value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// DataTables writes column output with innerHTML. Sanitise every column's
// display output: rendered columns through the HTML sanitiser, plain data
// columns as escaped text.
export const secureColumns = (columns) => columns.map((column) => {
    const original = column.render;
    const sanitize = column.className === 'actions' ? sanitizeActionHtml : sanitizeCardHtml;
    const render = original
        ? (data, type, row, meta) => {
            const out = original(data, type, row, meta);
            return type === 'display' && typeof out === 'string' ? sanitize(out) : out;
        }
        : (data, type) => (type === 'display' ? escapeHtml(data) : data);
    return { ...column, render };
});

// Card fields that hold HTML, as the API returns them. Used to clean a stored
// card before it is loaded into an editor (Quill parses HTML with innerHTML).
export const CARD_HTML_FIELDS = ['question', 'answer', 'extra', 'header', 'question_overlay', 'answer_overlay', 'axon_learn_more', 'axon_quiz_me', 'axon_quiz_me_answer'];
export const cleanCardHtml = (card) => {
    if (!card) return card;
    const clean = { ...card };
    CARD_HTML_FIELDS.forEach((field) => {
        if (typeof clean[field] === 'string') clean[field] = sanitizeCardHtml(clean[field]);
    });
    if (typeof clean.css === 'string') clean.css = sanitizeCardCss(clean.css);
    return clean;
};
